import {Component, ComponentRef, ElementRef, OnInit, ViewChild} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {ActivatedRoute, Params, Router} from '@angular/router';

import {TranslateService} from '@ngx-translate/core';

import {EMPTY, forkJoin, merge, Observable, of, Subject} from 'rxjs';
import {catchError, debounceTime, delay, filter, finalize, map, share, switchMap, takeUntil, tap} from 'rxjs/operators';

import {getOldWorkflowsConfig} from './charts/old-workflow-config';
import {getWorkflowByStatusChartConfig} from './charts/workflow-by-status-config';
import {getWorkflowByTypeChartConfig} from './charts/workflow-by-type-config';

import {ToastrService} from 'ngx-toastr';
import {ClipboardService} from 'ngx-clipboard';
import {Destroyable} from '../destroyable.component';
import {RolesService} from '../shared/authentication/roles/roles.service';
import {LocalizationService} from '../shared/localization/localization.service';
import {ModalService} from '../shared/modal';
import {DefaultPagination, Pagination} from '../shared/ui';
import {buildFiltersQuery, buildQueryParams} from '../shared/utils/objects';
import {Scss} from '../shared/utils/scss';
import {CancelWorkflowDetails} from '../shared/workflow/cancel-workflow-modal/cancel-workflow-modal.component';
import {
  ReassignRequestPopupComponent
} from '../shared/workflow/reassign-request-popup/reassign-request-popup.component';
import {Request, RequestStatus} from '../shared/workflow/workflow-v2.interface';
import {workflows_per_page, WorkflowV2Service} from '../shared/workflow/workflow-v2.service';

// Delay (ms) before refetching requests after a cancellation, to let the
// backend finish processing the asynchronous cancellation.
const WORKFLOW_CANCEL_REFRESH_DELAY_MS = 1500;

@Component({
  selector: 'gp-workflows-admin',
  templateUrl: './workflows-admin.component.html',
  styleUrls: ['./workflows-admin.component.scss']
})
export class WorkflowsAdminComponent extends Destroyable implements OnInit {

  public static ACCESS_NAME = 'WorkflowAdmin';

  @ViewChild('tableScrollContainer') private tableScrollContainer: ElementRef<HTMLElement>;

  public WORKFLOW_STATUS = [
    {name: 'WORKFLOWS.in_progress', key: RequestStatus.IN_PROGRESS},
    {name: 'WORKFLOWS.validated', key: RequestStatus.VALIDATED},
    {name: 'WORKFLOWS.rejected', key: RequestStatus.REJECTED},
    {name: 'WORKFLOWS.canceled', key: RequestStatus.CANCELED},
    {name: 'WORKFLOWS.deleted', key: RequestStatus.DELETED},
    {name: 'WORKFLOWS.error', key: RequestStatus.ERROR}
  ];
  public workflowTypes: { key: string }[];
  public requests: Request[];
  public loading: boolean = false;
  public pagination: Pagination = new DefaultPagination(workflows_per_page);
  public trigger$: Observable<number>;
  public selectedRequest: Request;
  public isCopied = false;
  public filterFormGroup: UntypedFormGroup = this.formBuilder.group({
    type: [''],
    subject: [''],
    status: [''],
    assignee: [''],
    requester: [''],
    createdBeyondMonth: ['']
  });
  public requestDetails$ = new Subject<Request>();
  public isLimitExceeded = false;

  public isWorkflowAdmin: boolean;
  public selectedRequestToAssign: Request;
  public reassigning: boolean;
  public showCharts = true;
  public workflowByTypeChartConfig: any;
  public workflowByStatusChartConfig: any;
  public oldWorkflowChartConfig: any;
  public cancelWorkflowDetails: CancelWorkflowDetails;

  private cacheRequests = {};
  private isCancellingWorkflow = false;
  public resolvedStatusColors: {[key: string]: string} = {};
  private statusColorCssVariables: {[key: string]: string} = {
    [RequestStatus.ERROR]: 'chart-workflow-error',
    [RequestStatus.IN_PROGRESS]: 'chart-workflow-in-progress',
    [RequestStatus.VALIDATED]: 'chart-workflow-validated',
    [RequestStatus.REJECTED]: 'chart-workflow-rejected',
    [RequestStatus.CANCELED]: 'chart-workflow-canceled',
    [RequestStatus.DELETED]: 'chart-workflow-deleted',
    updatePersonalInfo: 'chart-type-update-personal-info',
    updateMssSubordinateInfo: 'chart-type-update-mss-subordinate',
    onboarding: 'chart-type-onboarding',
    newOnboarding: 'chart-type-new-onboarding',
    new_onboarding: 'chart-type-new-onboarding',
    terminateContract: 'chart-type-terminate-contract',
    sendDocument: 'chart-type-send-document',
    mssHiring: 'chart-type-mss-hiring',
    essClaim: 'chart-type-ess-claim',
    essAbsence: 'chart-type-ess-absence',
    hrAbsence: 'chart-type-hr-absence',
    expensesRequest: 'chart-type-expenses-request'
  };
  private workflowByTypeData: {key?: number} = {};
  private workflowByStatusData: {[key in RequestStatus]?: number} = {};
  private oldWorkflowsChartData: {key?: number} = {};

  constructor(private workflowsV2Service: WorkflowV2Service,
              private rolesService: RolesService,
              private localizationService: LocalizationService,
              private translate: TranslateService,
              private modalService: ModalService,
              private toastrService: ToastrService,
              private formBuilder: UntypedFormBuilder,
              private router: Router,
              private route: ActivatedRoute,
              private clipboardService: ClipboardService
  ) {
    super();
  }

  ngOnInit(): void {
    this.workflowsV2Service.getWorkflowTypes().pipe(
      tap((types: string[]) => this.workflowTypes = types.map(type => ({key: type}))),
      switchMap(() => this.getChartsData()),
    ).subscribe();

    this.trigger$ = merge(
      this.localizationService.changeDateFormatEvent,
      this.translate.onLangChange
    ).pipe(
      tap(() => this.setChartsConfigs()),
      map(() => Math.random()), share());

    this.pagination.pageChanged
      .asObservable()
      .pipe(
        switchMap(() => this.getRequestsData()),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();

    this.filterFormGroup.valueChanges
      .pipe(
        tap(() => this.requestDetails$.next(null)),
        debounceTime(400),
        tap(() => {
          const filterValues = this.filterFormGroup.getRawValue();
          this.updateRoute({...filterValues, createdBeyondMonth: filterValues.createdBeyondMonth || ''});
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();

    this.route.queryParams
      .pipe(
        filter((params: Params) => !!params),
        tap((params: Params) => {
          this.filterFormGroup.setValue({
            type: params.type || '',
            requester: params.requester || '',
            subject: params.subject || '',
            status: params.status || '',
            assignee: params.assignee || '',
            createdBeyondMonth: params.createdBeyondMonth || ''
          });
          if (Object.keys(params).length > 0) {
            this.filterFormGroup.markAsDirty();
          } else {
            this.filterFormGroup.markAsPristine();
          }
        }),
        switchMap(() => this.getRequestsData()),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();

    this.requestDetails$
      .asObservable()
      .pipe(
        switchMap((request: Request) => {
          if (!request) {
            this.selectedRequest = null;
            return EMPTY;
          } else {
            return this.retrieveRequestDetails(request)
              .pipe(tap((selectedRequest: Request) => this.selectedRequest = selectedRequest));
          }
        }),
        takeUntil(this.componentDestroyed$)
      ).subscribe();

    this.isWorkflowAdmin = this.rolesService.hasRole('WORKFLOW_ADMIN');
  }

  public onClickRequest(request: Request, event: MouseEvent): void {
    const targetElement = <HTMLElement>event.target;
    const isContextMenuButton = targetElement?.closest('button')?.classList?.contains('btn-context-menu');
    const isDropdownItem = targetElement?.closest('li')?.classList?.contains('dropdown-item');
    if (!isContextMenuButton && !isDropdownItem) {
      this.requestDetails$.next(request);
    }
  }

  private getRequestsData(): Observable<{ total: number, isLimitExceeded: boolean, data: Request[] }> {
    this.loading = true;
    this.cacheRequests = {};

    const search = buildFiltersQuery(this.filterFormGroup.getRawValue());
    return this.workflowsV2Service.getWorkflowsInstances(this.pagination.currentPage.number, this.pagination.perPage, search)
      .pipe(
        tap(response => {
          this.requests = response.data.map(req =>
            ({
              ...req,
              isReassignable: req.status === 'IN_PROGRESS' &&
                !((req.type === 'onboarding' || req.type === 'new_onboarding') &&
                  req.subject?.employeeNumber === req.assignee?.employeeNumber)
            }));
          this.isLimitExceeded = response.isLimitExceeded;
          for (const name of ['type', 'status']) {
            const control = this.filterFormGroup.get(name);
            if (this.isLimitExceeded) {
              control.disable({emitEvent: false});
            } else {
              control.enable({emitEvent: false});
            }
          }
          this.pagination.update(response.total);
        }),
        finalize(() => this.loading = false)
      );
  }

  private updateRoute(filterValues: { [key: string]: string }): void {
    this.pagination.currentPage.number = 0;
    const queryParams = buildQueryParams(filterValues);
    this.router.navigate(['/workflows/admin'], {queryParams});
  }

  public toggleTooltip(event: MouseEvent, isCopied: boolean) {
    event.stopPropagation();
    this.isCopied = isCopied;
  }

  public copyRequestId(event: MouseEvent, requestId: string): void {
    this.clipboardService.copy(requestId);
    this.toggleTooltip(event, true);
  }

  public resetFilters() {
    this.filterFormGroup.reset();
  }

  private retrieveRequestDetails(request: Request): Observable<Request> {
    if (!this.cacheRequests[request.id]) {
      return this.workflowsV2Service.getRequestDetails(request.id)
        .pipe(
          tap(({steps}) => this.cacheRequests[request.id] = {...request, steps}),
          map(() => this.cacheRequests[request.id])
        );
    } else {
      return of(this.cacheRequests[request.id]);
    }
  }

  public openReassignRequestModal(request: Request) {
    this.selectedRequestToAssign = request;
    this.modalService.openModalWithName('ReassignRequestPopupComponent')
      .then((modalRef: ComponentRef<ReassignRequestPopupComponent>) => {
        modalRef.instance.reassigning = this.reassigning;
        modalRef.instance.reassign = this.reassignRequest.bind(this);
      });
  }

  public reassignRequest(modalRef: any, selectedUser: number) {
    if (selectedUser != null) {
      this.reassigning = true;
      this.workflowsV2Service.reassignRequest(this.selectedRequestToAssign.id, selectedUser.toString()).pipe(
        tap(() => {
          modalRef.close();
        }),
        catchError(() => {
          this.toastrService.error(this.translate.instant('WORKFLOWS.request_has_not_been_reassigned'));
          return EMPTY;
        }),
        finalize(() => {
          this.reassigning = false;
          this.selectedRequestToAssign = null;
        }),
        switchMap(data => {
          if (data) {
            this.toastrService.success(this.translate.instant('WORKFLOWS.request_has_been_reassigned'));
            delete this.cacheRequests[this.selectedRequestToAssign.id];
            return this.getRequestsData();
          }
        })
      ).subscribe();
    }
  }

  public closeSidePanel(isSidePanelOpenChange: boolean) {
    if (!isSidePanelOpenChange) {
      this.requestDetails$.next(null);
    }
  }

  public toggleCharts() {
    this.showCharts = !this.showCharts;
  }

  public getChartsData(): Observable<any> {
    return forkJoin([
      this.workflowsV2Service.getWorkflowsByTypeChartData(),
      this.workflowsV2Service.getWorkflowsByStatusChartData(),
      this.workflowsV2Service.getOldWorkflowsData(),
    ]).pipe(
      tap(([workflowByTypeData, workflowByStatusData, oldWorkflowData] ) => {
        this.resolveCssColors();
        this.workflowByTypeData = workflowByTypeData;
        this.workflowByStatusData = workflowByStatusData;
        this.oldWorkflowsChartData = oldWorkflowData;
        this.setChartsConfigs();
      })
    );
 }

  private resolveCssColors(): void {
    for (const status of this.WORKFLOW_STATUS) {
      const cssVarName = this.statusColorCssVariables[status.key as RequestStatus];
      if (cssVarName) {
        let color = Scss.variable(cssVarName);
        if (color) {
          color = color.trim();
        }
        this.resolvedStatusColors[status.key as RequestStatus] = color;
      } else {
        console.warn(`No CSS variable mapping found for status key: ${status.key}`);
      }
    }

    for (const type of this.workflowTypes) {
      const cssVarName = this.statusColorCssVariables[type.key];
      if (cssVarName) {
        let color = Scss.variable(cssVarName);
        if (color) {
          color = color.trim();
        }
        this.resolvedStatusColors[type.key] = color;
      } else {
        console.warn(`No CSS variable mapping found for workflow type: ${type}`);
      }
    }
  }


  public setChartsConfigs() {
    if (!this.workflowByTypeData || !this.workflowByStatusData || !this.oldWorkflowsChartData || !this.workflowTypes) {
      return;
    }
    this.workflowByTypeChartConfig =
      getWorkflowByTypeChartConfig(this.workflowByTypeData, this.workflowTypes, this.resolvedStatusColors, this.translate);
    this.workflowByStatusChartConfig =
      getWorkflowByStatusChartConfig(this.workflowByStatusData, this.WORKFLOW_STATUS, this.resolvedStatusColors, this.translate);
    this.oldWorkflowChartConfig =
      getOldWorkflowsConfig(this.workflowTypes, this.oldWorkflowsChartData, this.resolvedStatusColors, this.translate);
  }

  public onTypeClick(event: any) {
    const workflowType = this.workflowTypes.find(type => this.translate.instant(type.key) === event.name)?.key;
    this.filterFormGroup.setValue(
    {
      type: workflowType,
      requester: '',
      subject: '',
      status: '',
      assignee: '',
      createdBeyondMonth: ''
    });
  }

  public onStatusClick(event: any) {
    const workflowStatus = this.WORKFLOW_STATUS.find(status => this.translate.instant(status.name) === event.name)?.key;
    this.filterFormGroup.setValue(
      {
        type: '',
        requester: '',
        subject: '',
        status: workflowStatus,
        assignee: '',
        createdBeyondMonth: ''
      });
  }

  public onOldWorkflowsClick(event: any) {
    const workflowType = event.type;
    this.filterFormGroup.setValue(
      {
        type: workflowType ? workflowType : '',
        requester: '',
        subject: '',
        status: RequestStatus.IN_PROGRESS,
        assignee: '',
        createdBeyondMonth: true
      });
  }

  public openCancelWorkflowModal(request: Request, event: Event, contextMenu?: { opened: boolean }) {
    event.stopPropagation();
    if (contextMenu) {
      contextMenu.opened = false;
    }
    this.cancelWorkflowDetails = {
      workflowType: request.type,
      workflowAssignee: request.assignee?.fullName,
      workflowAssignees: request.assignee
        ? [{fullName: request.assignee.fullName, employeeNumber: request.assignee.employeeNumber}]
        : (request.candidates || []).map(candidate => ({
          fullName: candidate.fullName,
          employeeNumber: candidate.employeeNumber
        })),
      workflowStatus: request.status,
      workflowSubject: request.subject?.fullName,
      subjectEmployeeNumber: request.subject?.employeeNumber,
      assigneeEmployeeNumber: request.assignee?.employeeNumber,
      requestId: request.id
    };
    this.modalService.openModalWithId('cancel-workflow');
  }

  public cancelWorkflow(cancelableRequest: {id: string, comment: string}) {
    if (this.isCancellingWorkflow) {
      return;
    }
    this.isCancellingWorkflow = true;
    const cancellingToast = this.toastrService.info(this.translate.instant('WORKFLOWS.cancellation_in_progress'), undefined, {
      disableTimeOut: true,
      tapToDismiss: false,
      closeButton: false
    });
    const scrollTop = this.getTableScrollTop();
    this.workflowsV2Service.cancelWorkflow(cancelableRequest.id, cancelableRequest.comment).pipe(
      finalize(() => {
        if (cancellingToast) {
          this.toastrService.clear(cancellingToast.toastId);
        }
      }),
      tap(() => {
        this.toastrService.success(this.translate.instant('WORKFLOWS.request_has_been_canceled'));
        this.loading = true;
      }),
      delay(WORKFLOW_CANCEL_REFRESH_DELAY_MS),
      switchMap(() => this.getRequestsData()),
      tap(() => this.restoreTableScrollTop(scrollTop)),
      catchError(() => {
        this.toastrService.error(this.translate.instant('WORKFLOWS.request_has_not_been_canceled'));
        return EMPTY;
      }),
      finalize(() => this.isCancellingWorkflow = false),
      takeUntil(this.componentDestroyed$)
      )
      .subscribe();

  }

  private getTableScrollTop(): number {
    return this.tableScrollContainer?.nativeElement?.scrollTop ?? 0;
  }

  private restoreTableScrollTop(scrollTop: number): void {
    setTimeout(() => {
      if (this.tableScrollContainer?.nativeElement) {
        this.tableScrollContainer.nativeElement.scrollTop = scrollTop;
      }
    });
  }
}
